import React, { useState } from 'react';
import Header from '../components/common/Header.jsx';
import Footer from '../components/common/Footer.jsx';
// import DocumentDownload from '../components/legal/DocumentDownload.jsx';
import { useTheme } from '../context/ThemeContext.jsx';

// The exact wording the user must affirm. Stored verbatim in D1 with each
// submission so the legal record reflects what they actually saw.
const ATTESTATION_TEXT_OPT_IN =
  'I confirm that I am the owner of this phone number and I expressly consent to receive SMS notifications from LondaLab, including order updates and account notifications. Message and data rates may apply. I understand I can opt out at any time by replying STOP.';

const ATTESTATION_TEXT_OPT_OUT =
  'I confirm that I am the owner of this phone number and I am requesting to stop receiving SMS notifications from LondaLab. I understand this request applies only to the phone number provided.';

function SMSOptForm({ action, attestationText, theme, onClose }) {
  const { colors, spacing, fonts, borderRadius } = theme;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [info, setInfo] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const isOptIn = action === 'opt_in';

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed || status.state === 'submitting') return;
    setStatus({ state: 'submitting', message: '' });
    try {
      const res = await fetch('/api/sms-opt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          name,
          email,
          phone,
          additional_info: info,
          attestation: true,
          attestation_text: attestationText,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus({ state: 'error', message: data.error || 'Submission failed' });
        return;
      }
      setStatus({
        state: 'success',
        message: isOptIn
          ? 'Your opt-in request has been recorded. You will receive a confirmation message shortly.'
          : 'Your opt-out request has been recorded. You will be removed from SMS communications.',
      });
    } catch (err) {
      setStatus({ state: 'error', message: 'Network error. Please try again.' });
    }
  };

  const fieldStyles = {
    width: '100%',
    padding: spacing.scale.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    fontSize: fonts.sizes.base.desktop,
    fontFamily: fonts.families.primary,
    boxSizing: 'border-box',
    marginTop: spacing.scale.xs,
  };
  const labelStyles = {
    display: 'block',
    fontSize: fonts.sizes.sm.desktop,
    color: colors.textSecondary,
    fontWeight: fonts.weights.medium,
    marginTop: spacing.scale.md,
  };
  const submitStyles = {
    backgroundColor: agreed && status.state !== 'submitting'
      ? (isOptIn ? colors.secondary : colors.error)
      : colors.surfaceHover,
    color: agreed && status.state !== 'submitting'
      ? colors.textOnAccent
      : colors.textMuted,
    border: 'none',
    padding: `${spacing.scale.md} ${spacing.scale.xl}`,
    borderRadius: borderRadius.md,
    fontSize: fonts.sizes.base.desktop,
    fontWeight: fonts.weights.semibold,
    cursor: agreed && status.state !== 'submitting' ? 'pointer' : 'not-allowed',
    width: '100%',
    marginTop: spacing.scale.lg,
    transition: 'all 0.2s ease',
  };

  if (status.state === 'success') {
    return (
      <div style={{
        backgroundColor: `${colors.success}15`,
        border: `1px solid ${colors.success}`,
        borderRadius: borderRadius.md,
        padding: spacing.scale.lg,
        marginTop: spacing.scale.lg,
      }}>
        <p style={{ color: colors.text, margin: 0, lineHeight: fonts.lineHeights.relaxed }}>
          {status.message}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{
      backgroundColor: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md,
      padding: spacing.scale.lg,
      marginTop: spacing.scale.lg,
    }}>
      <label style={labelStyles}>
        Name <span style={{ color: colors.error }}>*</span>
        <input style={fieldStyles} type="text" required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label style={labelStyles}>
        Email <span style={{ color: colors.error }}>*</span>
        <input style={fieldStyles} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label style={labelStyles}>
        Phone <span style={{ color: colors.error }}>*</span>
        <input style={fieldStyles} type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} />
      </label>
      <label style={labelStyles}>
        Additional Information
        <textarea
          style={{ ...fieldStyles, minHeight: '80px', resize: 'vertical' }}
          value={info}
          onChange={(e) => setInfo(e.target.value)}
        />
      </label>

      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing.scale.sm,
        marginTop: spacing.scale.lg,
        padding: spacing.scale.md,
        backgroundColor: colors.backgroundSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: borderRadius.md,
      }}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          style={{ marginTop: '4px', flexShrink: 0 }}
        />
        <span style={{
          fontSize: fonts.sizes.sm.desktop,
          color: colors.text,
          lineHeight: fonts.lineHeights.relaxed,
        }}>
          {attestationText}
        </span>
      </label>

      {status.state === 'error' && (
        <p style={{
          color: colors.error,
          fontSize: fonts.sizes.sm.desktop,
          marginTop: spacing.scale.md,
          marginBottom: 0,
        }}>
          {status.message}
        </p>
      )}

      <button type="submit" disabled={!agreed || status.state === 'submitting'} style={submitStyles}>
        {status.state === 'submitting'
          ? 'Submitting...'
          : isOptIn ? 'Submit Opt-In Request' : 'Submit Opt-Out Request'}
      </button>

      <button
        type="button"
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: colors.textMuted,
          fontSize: fonts.sizes.sm.desktop,
          cursor: 'pointer',
          marginTop: spacing.scale.md,
          textDecoration: 'underline',
        }}
      >
        Cancel
      </button>
    </form>
  );
}

const SMSInformation = () => {
  const theme = useTheme();
  const {
    colors,
    spacing,
    fonts,
    borderRadius,
    shadows
  } = theme;
  const [optInOpen, setOptInOpen] = useState(false);
  const [optOutOpen, setOptOutOpen] = useState(false);

  const pageStyles = {
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.families.primary,
    lineHeight: fonts.lineHeights.relaxed,
    transition: 'all 0.3s ease'
  };

  const mainStyles = {
    maxWidth: '800px',
    margin: `${spacing.scale['2xl']} auto`,
    padding: `0 ${spacing.scale.lg}`
  };

  const contentContainerStyles = {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.xl,
    boxShadow: shadows.xl,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`
  };

  const headerSectionStyles = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: colors.textInverse,
    padding: `${spacing.scale.lg} ${spacing.scale.md}`,
    textAlign: 'center'
  };

  const titleStyles = {
    fontSize: fonts.sizes.xl.desktop,
    marginBottom: spacing.scale.sm,
    fontWeight: fonts.weights.bold,
    fontFamily: fonts.families.secondary,
    color: '#ffffff',
    textShadow: `1px 1px 2px rgba(0, 0, 0, 0.5)`
  };

  const updatedStyles = {
    fontSize: fonts.sizes.sm.desktop,
    opacity: 0.95,
    backgroundColor: `${colors.info}33`,
    padding: `${spacing.scale.xs} ${spacing.scale.md}`,
    borderRadius: borderRadius.full,
    display: 'inline-block',
    border: `1px solid ${colors.info}66`,
    color: '#ffffff',
    marginBottom: spacing.scale.xs
  };

  const subtitleStyles = {
    fontSize: fonts.sizes.base.desktop,
    opacity: 0.95,
    fontWeight: fonts.weights.regular,
    color: '#ffffff'
  };

  const contentStyles = {
    padding: `${spacing.scale.lg} ${spacing.scale.xl}`
  };

  const highlightBoxStyles = {
    backgroundColor: `${colors.info}15`,
    border: `1px solid ${colors.info}`,
    borderRadius: borderRadius.lg,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.xl} 0`
  };

  const highlightBoxSuccessStyles = {
    backgroundColor: `${colors.success}15`,
    border: `1px solid ${colors.success}`,
    borderRadius: borderRadius.lg,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.xl} 0`
  };

  const highlightBoxWarningStyles = {
    backgroundColor: `${colors.warning}15`,
    border: `2px solid ${colors.warning}`,
    borderRadius: borderRadius.lg,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.xl} 0`,
    textAlign: 'center'
  };

  const sectionStyles = {
    marginBottom: spacing.scale['3xl']
  };

  const sectionHeadingStyles = {
    color: colors.text,
    fontSize: fonts.sizes['2xl'].desktop,
    marginBottom: spacing.scale.xl,
    paddingBottom: spacing.scale.sm,
    borderBottom: `2px solid ${colors.border}`,
    fontFamily: fonts.families.secondary,
    fontWeight: fonts.weights.semibold
  };

  const subHeadingStyles = {
    color: colors.textSecondary,
    fontSize: fonts.sizes.xl.desktop,
    margin: `${spacing.scale.xl} 0 ${spacing.scale.lg} 0`,
    fontWeight: fonts.weights.medium
  };

  const paragraphStyles = {
    marginBottom: spacing.scale.xl,
    fontSize: fonts.sizes.base.desktop,
    color: colors.text,
    lineHeight: fonts.lineHeights.relaxed
  };

  const listStyles = {
    marginBottom: spacing.scale.xl,
    paddingLeft: spacing.scale.xl,
    color: colors.text
  };

  const listItemStyles = {
    marginBottom: spacing.scale.md
  };

  const optOutCodesStyles = {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: borderRadius.md,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.lg} 0`,
    fontFamily: fonts.families.mono,
    fontSize: fonts.sizes.lg.desktop,
    fontWeight: fonts.weights.bold,
    textAlign: 'center',
    border: `2px dashed ${colors.border}`,
    color: colors.text
  };

  const contactInfoStyles = {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: borderRadius.lg,
    padding: spacing.scale.xl,
    marginTop: spacing.scale['3xl'],
    textAlign: 'center',
    border: `1px solid ${colors.border}`
  };

  const contactTitleStyles = {
    color: colors.text,
    marginBottom: spacing.scale.lg,
    fontSize: fonts.sizes.xl.desktop,
    fontWeight: fonts.weights.semibold,
    fontFamily: fonts.families.secondary
  };

  const contactLinkStyles = {
    color: colors.secondary,
    textDecoration: 'none',
    fontWeight: fonts.weights.medium,
    transition: 'color 0.3s ease'
  };

  const buttonStyles = {
    backgroundColor: colors.secondary,
    color: colors.textOnAccent,
    border: 'none',
    padding: `${spacing.scale.md} ${spacing.scale.xl}`,
    borderRadius: borderRadius.md,
    fontSize: fonts.sizes.base.desktop,
    fontWeight: fonts.weights.medium,
    cursor: 'pointer',
    marginRight: spacing.scale.sm,
    marginBottom: spacing.scale.sm,
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'inline-block'
  };

  const optOutButtonStyles = {
    ...buttonStyles,
    backgroundColor: colors.error
  };

  const handleLinkHover = (e) => {
    e.target.style.color = colors.secondaryHover;
  };

  const handleLinkLeave = (e) => {
    e.target.style.color = colors.secondary;
  };

  const handleButtonHover = (e) => {
    e.target.style.backgroundColor = colors.secondaryHover;
    e.target.style.transform = 'translateY(-2px)';
  };

  const handleButtonLeave = (e) => {
    e.target.style.backgroundColor = colors.secondary;
    e.target.style.transform = 'translateY(0)';
  };

  const handleOptOutButtonHover = (e) => {
    e.target.style.backgroundColor = colors.accentHover;
    e.target.style.transform = 'translateY(-2px)';
  };

  const handleOptOutButtonLeave = (e) => {
    e.target.style.backgroundColor = colors.error;
    e.target.style.transform = 'translateY(0)';
  };

  return (
    <div style={pageStyles}>
      <Header showBreadcrumb={false} />
      
      <main style={mainStyles}>
        <div style={contentContainerStyles}>
          <section style={headerSectionStyles}>
            <h1 style={titleStyles}>SMS Preferences</h1>
            <div style={updatedStyles}>Last Updated: July 16, 2026</div>
            <p style={subtitleStyles}>Manage your text message communications with LondaLab</p>
            {/* <div style={{ marginTop: spacing.scale.sm }}>
              <DocumentDownload
                document={{
                  name: "SMS Information",
                  filename: "sms-information",
                  type: "sms"
                }}
              />
            </div> */}
          </section>

          <div style={contentStyles}>
            <div style={highlightBoxStyles}>
              <h3 style={{ 
                color: colors.info, 
                marginTop: 0, 
                marginBottom: spacing.scale.md,
                fontSize: fonts.sizes.lg.desktop,
                fontWeight: fonts.weights.semibold 
              }}>
                TCPA Compliant SMS Program
              </h3>
              <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                We operate our SMS program in full compliance with the Telephone Consumer Protection Act (TCPA) and all applicable federal and state regulations. Your privacy and consent are our top priorities.
              </p>
            </div>

            <section style={sectionStyles} id="what-we-send">
              <h2 style={sectionHeadingStyles}>SMS Program Details</h2>
              
              <h3 style={subHeadingStyles}>Program Description</h3>
              <p style={paragraphStyles}>
                <strong>LondaLab SMS Program:</strong> Receive important order updates and notifications about your purchases via text message.
              </p>
              
              <h3 style={subHeadingStyles}>Message Types & Frequency</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Order Confirmations:</strong> Confirmation that we received your order</li>
                <li style={listItemStyles}><strong>Payment Notifications:</strong> Payment processing updates and confirmations</li>
                <li style={listItemStyles}><strong>Shipping Updates:</strong> When your order ships with tracking information</li>
                <li style={listItemStyles}><strong>Delivery Notifications:</strong> When your package is delivered</li>
                <li style={listItemStyles}><strong>Order Issues:</strong> Problems with your order that need your attention</li>
                <li style={listItemStyles}><strong>Digital Delivery:</strong> Access links and download instructions for software/content</li>
              </ul>

              <div style={{ 
                backgroundColor: `${colors.warning}15`, 
                border: `1px solid ${colors.warning}`, 
                borderRadius: borderRadius.lg, 
                padding: spacing.scale.xl, 
                margin: `${spacing.scale.xl} 0` 
              }}>
                <h3 style={{ 
                  color: colors.warning, 
                  marginTop: 0, 
                  marginBottom: spacing.scale.md,
                  fontSize: fonts.sizes.lg.desktop,
                  fontWeight: fonts.weights.semibold 
                }}>
                  Message Frequency
                </h3>
                <p style={paragraphStyles}>
                  <strong>Frequency:</strong> Messages sent only when you place orders or there are updates to your existing orders. Typically 2-4 messages per order (confirmation, shipping, delivery).
                </p>
                <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                  <strong>No promotional messages:</strong> We only send order-related notifications, never marketing or promotional content.
                </p>
              </div>

              <h3 style={subHeadingStyles}>Cost Information</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Message & data rates apply:</strong> Standard messaging rates from your carrier</li>
                <li style={listItemStyles}><strong>No premium charges:</strong> We do not charge premium rates for our messages</li>
                <li style={listItemStyles}><strong>Free to opt-out:</strong> STOP messages are always free to send</li>
              </ul>
            </section>

            <section style={sectionStyles} id="opt-in">
              <h2 style={sectionHeadingStyles}>Opt-In to SMS Communications</h2>
              
              <p style={paragraphStyles}>Want to receive helpful updates via text? Sign up below:</p>
              
              <div style={highlightBoxSuccessStyles}>
                <h3 style={{ 
                  color: colors.success, 
                  marginTop: 0, 
                  marginBottom: spacing.scale.md,
                  fontSize: fonts.sizes.lg.desktop,
                  fontWeight: fonts.weights.semibold 
                }}>
                  Ready to Subscribe?
                </h3>
                <p style={paragraphStyles}>Click the button below to opt-in to SMS notifications:</p>
                <div style={{ textAlign: 'center', marginTop: spacing.scale.lg }}>
                  <button
                    type="button"
                    onClick={() => setOptInOpen((v) => !v)}
                    style={buttonStyles}
                    onMouseEnter={handleButtonHover}
                    onMouseLeave={handleButtonLeave}
                  >
                    {optInOpen ? '✅ Hide Opt-In Form' : '✅ Opt-In to SMS Notifications'}
                  </button>
                </div>
                {optInOpen && (
                  <SMSOptForm
                    action="opt_in"
                    attestationText={ATTESTATION_TEXT_OPT_IN}
                    theme={theme}
                    onClose={() => setOptInOpen(false)}
                  />
                )}
              </div>
            </section>

            <section style={sectionStyles} id="opt-out">
              <h2 style={sectionHeadingStyles}>Opt-Out of SMS Communications</h2>
              
              <p style={paragraphStyles}>No longer want to receive text messages from us? No problem - we make it easy to opt-out.</p>

              <div style={highlightBoxWarningStyles}>
                <h3 style={{ 
                  color: colors.warning, 
                  marginTop: 0, 
                  marginBottom: spacing.scale.md,
                  fontSize: fonts.sizes.lg.desktop,
                  fontWeight: fonts.weights.semibold 
                }}>
                  Quick Opt-Out Methods
                </h3>
                <p style={paragraphStyles}><strong>Fastest way:</strong> Reply to any of our text messages with one of these codes:</p>
                
                <div style={optOutCodesStyles}>
                  STOP • QUIT • UNSUBSCRIBE • CANCEL • END
                </div>
                
                <p style={{ 
                  ...paragraphStyles, 
                  fontSize: fonts.sizes.sm.desktop, 
                  marginBottom: 0 
                }}>
                  You'll receive one confirmation message, then no more texts from us.
                </p>
              </div>

              <h3 style={subHeadingStyles}>Opt-Out Online</h3>
              <div style={{ 
                backgroundColor: `${colors.warning}15`, 
                border: `1px solid ${colors.warning}`, 
                borderRadius: borderRadius.lg, 
                padding: spacing.scale.xl, 
                margin: `${spacing.scale.xl} 0` 
              }}>
                <p style={paragraphStyles}>You can also opt-out using our online form:</p>
                <div style={{ textAlign: 'center', marginTop: spacing.scale.lg }}>
                  <button
                    type="button"
                    onClick={() => setOptOutOpen((v) => !v)}
                    style={optOutButtonStyles}
                    onMouseEnter={handleOptOutButtonHover}
                    onMouseLeave={handleOptOutButtonLeave}
                  >
                    {optOutOpen ? '❌ Hide Opt-Out Form' : '❌ Opt-Out of SMS Notifications'}
                  </button>
                </div>
                {optOutOpen && (
                  <SMSOptForm
                    action="opt_out"
                    attestationText={ATTESTATION_TEXT_OPT_OUT}
                    theme={theme}
                    onClose={() => setOptOutOpen(false)}
                  />
                )}
              </div>
            </section>

            <section style={sectionStyles} id="important-info">
              <h2 style={sectionHeadingStyles}>Important Legal Information</h2>
              
              <div style={highlightBoxStyles}>
                <h3 style={{ 
                  color: colors.info, 
                  marginTop: 0, 
                  marginBottom: spacing.scale.md,
                  fontSize: fonts.sizes.lg.desktop,
                  fontWeight: fonts.weights.semibold 
                }}>
                  Transactional SMS Program
                </h3>
                <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                  This SMS program sends order-related notifications only. These are transactional messages to keep you informed about your purchases, not marketing communications.
                </p>
              </div>

              <h3 style={subHeadingStyles}>Your Legal Rights</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Opt-out anytime:</strong> Text STOP to any message - it's always free</li>
                <li style={listItemStyles}><strong>Get help:</strong> Text HELP for customer service information</li>
                <li style={listItemStyles}><strong>No purchase required:</strong> Consent to SMS is not required to buy our products</li>
                <li style={listItemStyles}><strong>Dispute resolution:</strong> Contact us immediately with any SMS-related concerns</li>
                <li style={listItemStyles}><strong>Data retention:</strong> We keep opt-out records for compliance purposes</li>
              </ul>

              <h3 style={subHeadingStyles}>Message & Data Rates</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Standard rates apply:</strong> Your mobile carrier's standard messaging and data rates apply</li>
                <li style={listItemStyles}><strong>We don't charge extra:</strong> LondaLab doesn't charge for SMS messages</li>
                <li style={listItemStyles}><strong>Check your plan:</strong> Confirm you have a texting plan or unlimited texts</li>
                <li style={listItemStyles}><strong>STOP is free:</strong> Opt-out messages are never charged by us</li>
              </ul>

              <h3 style={subHeadingStyles}>Supported Carriers</h3>
              <p style={paragraphStyles}>Our SMS service works with all major US carriers including:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Major carriers:</strong> Verizon, AT&T, T-Mobile, Sprint</li>
                <li style={listItemStyles}><strong>Prepaid carriers:</strong> Cricket, Metro PCS, Boost Mobile, Straight Talk</li>
                <li style={listItemStyles}><strong>MVNOs:</strong> Google Fi, Mint Mobile, Visible, and most other carriers</li>
                <li style={listItemStyles}><strong>Geographic coverage:</strong> United States and Canada</li>
              </ul>

              <div style={highlightBoxSuccessStyles}>
                <h3 style={{ 
                  color: colors.success, 
                  marginTop: 0, 
                  marginBottom: spacing.scale.md,
                  fontSize: fonts.sizes.lg.desktop,
                  fontWeight: fonts.weights.semibold 
                }}>
                  Your Rights & Protections
                </h3>
                <ul style={{ ...listStyles, marginBottom: 0 }}>
                  <li style={listItemStyles}><strong>Immediate opt-out:</strong> STOP takes effect within 24 hours</li>
                  <li style={listItemStyles}><strong>No penalties:</strong> Opting out won't affect your account, purchases, or service</li>
                  <li style={listItemStyles}><strong>Privacy protection:</strong> Phone numbers are never sold or shared with third parties</li>
                  <li style={listItemStyles}><strong>Secure transmission:</strong> All messages sent through encrypted, compliant channels</li>
                  <li style={listItemStyles}><strong>Complaint resolution:</strong> Contact us for any SMS-related issues</li>
                </ul>
              </div>
            </section>

            <div style={contactInfoStyles}>
              <h3 style={contactTitleStyles}>SMS Program Support</h3>
              <p style={paragraphStyles}>Questions about SMS, need help opting out, or have compliance concerns?</p>
              <p style={paragraphStyles}>
                <strong>SMS Support:</strong> <a 
                  href="mailto:hello@londalab.com" 
                  style={contactLinkStyles}
                  onMouseEnter={handleLinkHover}
                  onMouseLeave={handleLinkLeave}
                >hello@londalab.com</a><br />
                <strong>General Support:</strong> <a 
                  href="mailto:hello@londalab.com" 
                  style={contactLinkStyles}
                  onMouseEnter={handleLinkHover}
                  onMouseLeave={handleLinkLeave}
                >hello@londalab.com</a><br />
                <strong>Legal/Compliance:</strong> <a 
                  href="mailto:hello@londalab.com" 
                  style={contactLinkStyles}
                  onMouseEnter={handleLinkHover}
                  onMouseLeave={handleLinkLeave}
                >hello@londalab.com</a><br />
                <strong>Text for Help:</strong> Reply HELP to any message
              </p>
              <p style={paragraphStyles}>
                <strong>LondaLab</strong>, a Victoria Londe brand<br />
                2883 Hopyard Rd<br />
                Pleasanton, CA 94588
              </p>
              <p style={paragraphStyles}>
                <strong>SMS Program Response Time:</strong> Within 24 hours for SMS-related issues
              </p>
              
              <div style={{ 
                marginTop: spacing.scale.lg, 
                padding: spacing.scale.md, 
                backgroundColor: colors.backgroundSecondary, 
                borderRadius: borderRadius.md, 
                borderLeft: `4px solid ${colors.secondary}` 
              }}>
                <h4 style={{ 
                  color: colors.text, 
                  marginTop: 0, 
                  marginBottom: spacing.scale.sm,
                  fontSize: fonts.sizes.base.desktop,
                  fontWeight: fonts.weights.semibold 
                }}>
                  Quick Reference:
                </h4>
                <p style={{ 
                  ...paragraphStyles, 
                  marginBottom: 0, 
                  fontSize: fonts.sizes.sm.desktop 
                }}>
                  <strong>To Opt-Out:</strong> Text STOP, QUIT, UNSUBSCRIBE, CANCEL, or END<br />
                  <strong>For Help:</strong> Text HELP or email hello@londalab.com<br />
                  <strong>Powered by:</strong> Twilio (compliant SMS platform)
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SMSInformation;